/**
 * Arvo's public addresses, in one place since Arvo moved to a domain of its own, eevo.io
 * (PLT-7477). On Eevo's own hosts the console is the root. On the platform's production, staging
 * and dev env hosts `/arvo` now sends people to Eevo's (`movedEevoOrigin`); local development
 * keeps the console under `/arvo`. Which one a page or a link is on follows from its host
 * (`isEevoHost`), so the same build serves both.
 *
 * The docs, code snippets and OpenAPI document name `ARVO_PRODUCTION_URL` in every environment, so
 * a snippet copied from staging still works. Links the server builds for a request (a decision's
 * result view, the OAuth issuer) use the running environment's origin instead: see
 * `common/arvo-public-url.ts` in the backend.
 */

/** The origin developers call the API on and open the console at, in production. */
export const ARVO_PRODUCTION_URL = 'https://eevo.io';

/**
 * The API's EU base URL in production: a request on it is served in the EU region and billed at the
 * EU price (`upserve/modules/arvo/engine/region.ts`).
 */
export const ARVO_EU_PRODUCTION_URL = 'https://eu.eevo.io';

/** Eevo's own domain. Its subdomains (`staging.eevo.io`) are Eevo's other environments. */
export const EEVO_DOMAIN = 'eevo.io';

/** The platform's hosts whose `/arvo` console has moved to Eevo's own host, and where to. */
const EEVO_ORIGIN_OF_PLATFORM_HOST: Record<string, string> = {
  'uprightplatform.com': 'https://eevo.io',
  'staging.uprightplatform.com': 'https://staging.eevo.io',
};

/** A dev env's platform host, `<env>.development.uprightplatform.io` (`devDomainName` in infra). */
const DEV_PLATFORM_HOST = /^([a-z0-9-]+)\.development\.uprightplatform\.io$/;

/**
 * Where the console on a platform host now lives, or null where it has not moved (local
 * development keeps the console under `/arvo`). A dev env's is `<env>.dev.eevo.io`
 * (`devEevoDomainName` in infra).
 */
export function movedEevoOrigin(platformHostname: string): string | null {
  const devEnv = DEV_PLATFORM_HOST.exec(platformHostname)?.[1];
  if (devEnv) return `https://${devEnv}.dev.${EEVO_DOMAIN}`;
  return EEVO_ORIGIN_OF_PLATFORM_HOST[platformHostname] ?? null;
}

/** The console's path on the platform's hosts. */
export const PLATFORM_ARVO_CONSOLE_PATH = '/arvo';

/** Whether `hostname` is one of Eevo's own hosts, where the console is the root of the site. */
export function isEevoHost(hostname: string): boolean {
  return hostname === EEVO_DOMAIN || hostname.endsWith(`.${EEVO_DOMAIN}`);
}

/**
 * Whether `hostname` is Eevo's status page (`status.eevo.io`, `status.staging.eevo.io`; PLT-7963),
 * which serves only that page.
 */
export function isEevoStatusHost(hostname: string): boolean {
  return hostname.startsWith('status.') && isEevoHost(hostname);
}

/** The console's path on a host: `/arvo` on the platform's, empty on Eevo's. */
export function arvoConsoleBasePath(hostname: string): string {
  return isEevoHost(hostname) ? '' : PLATFORM_ARVO_CONSOLE_PATH;
}

/** A console page on `origin`, e.g. `arvoConsoleUrl(origin, '/billing')`. */
export function arvoConsoleUrl(origin: string, path = ''): string {
  const root = origin.replace(/\/+$/, '');
  const rest = `${arvoConsoleBasePath(new URL(root).hostname)}${path}`;
  return `${root}${rest || '/'}`;
}

/**
 * A published decision's page, `/d/{token}` on Eevo's hosts (`upserve/modules/arvo/decisions/publication.ts`,
 * `views/arvo/public`): the one page of the console's that opens without signing in.
 */
export const PUBLIC_DECISION_PATH = '/d';
